import medianData from '../../json/median_income.json';
import { getTotalIncome } from './summaryGenerator';


const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia',
};

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL;

const bracketCode = (value, limits, codes) => {
  const amount = Number(value) || 0;
  const index = limits.findIndex(limit => amount < limit);
  return index === -1 ? codes[codes.length - 1] : codes[index];
};

const calcGrossWages = employment => {
  const rate = Number(employment.pay_rate) || 0;
  const units = Number(employment.pay_units) || 0;
  const factor = {
    hourly: (units * 52) / 12,
    weekly: 52 / 12,
    biweekly: 26 / 12,
    semimonthly: 2,
    monthly: 1,
    yearly: 1 / 12,
  }[employment.pay_schedule] || 0;
  return rate * factor;
};

async function fipsForDebtor(debtor) {
  const response = await fetch(`${BACKEND_API_URL}/api/geocode-fips`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      street: debtor.street,
      city: debtor.city,
      state: debtor.state,
      zip: debtor.zip_code,
    }),
  });
  if (!response.ok) throw new Error(`Could not determine the FIPS code for ${debtor.first_name}.`);
  const payload = await response.json();
  const attributes = payload?.tracts?.features?.[0]?.attributes;
  if (!attributes) throw new Error(`Could not determine the FIPS code for ${debtor.first_name}.`);
  return `${String(attributes.STATE).padStart(2, '0')}${String(attributes.COUNTY).padStart(3, '0')}`;
}

export async function buildDebtorTextBlob({
  caseData,
  petitionData,
  data,
  expenses,
  installments,
  meansTestIncomes,
  dependents,
}) {
  const feeStatus = caseData.filing_fee_method === 'waiver'
    ? 'w'
    : caseData.filing_fee_method === 'with_petition' ? 'p' : 'i';
  const debtType = caseData.debt_type === 'business'
    ? 'b'
    : caseData.debt_type === 'other' ? 'o' : 'c';
  const assetNotice = caseData.chapter === '7' ? 'n' : 'y';
  const numCreditors = bracketCode(
    petitionData.creditor_count,
    [50, 100, 200, 1000, 5001, 10001, 25001, 50001, 100001],
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'],
  );
  const totalDebtStat = bracketCode(
    petitionData.total_debt_amount,
    [50001, 100001, 500001, 1000001, 10000001, 50000001, 100000001, 500000001, 1000000001, 10000000001, 50000000001],
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M'],
  );
  const totalAssets = (Number(petitionData.real_property_total) || 0)
    + (Number(petitionData.personal_property_total) || 0);
  const assetAmountStat = bracketCode(
    totalAssets,
    [50001, 100001, 500001, 1000001, 10000001, 50000001, 100000001, 500000001, 1000000001, 10000000001, 50000000001],
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M'],
  );

  const priorBKStat = (petitionData.debtors || []).some(
    debtor => (debtor.prior_bankruptcies || []).length > 0,
  ) ? 'y' : 'n';
  const securedDebts = (data.debtsD || []).filter(debt => debt.debt_type === 'secured' && !debt.additional_notified);
  const priorityDebts = (data.debtsE || []).filter(debt => debt.debt_type === 'priority' && !debt.additional_notified);
  const unsecuredDebts = (data.debtsF || []).filter(debt => debt.debt_type === 'unsecured' && !debt.additional_notified);
  const sumBy = (items, field) => items.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
  const totalSecuredBalance = sumBy(securedDebts, 'balance');
  const totalPriorityBalance = sumBy(priorityDebts, 'balance');
  const totalUnsecuredBalance = sumBy(unsecuredDebts, 'balance');
  const totalIncomeStat = Number(getTotalIncome(caseData)) || 0;

  const totalExpense = [...(expenses || []), ...(installments || [])]
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const totalMonthly = income => (
    ['gross_wages', 'alimony', 'household_expenses', 'biz_net_monthly', 'real_net_monthly',
      'interest_dividends', 'unemployment', 'pension']
      .reduce((sum, key) => sum + (Number(income[key]) || 0), 0)
    + (income.other_incomes || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
  );
  const inc1 = (meansTestIncomes || []).find(income => income.debtor?.is_primary) || {};
  const inc2 = (meansTestIncomes || []).find(income => !income.debtor?.is_primary) || {};
  const m1 = totalMonthly(inc1);
  const m2 = totalMonthly(inc2);

  const otherUnsecuredTotal = type => sumBy(unsecuredDebts.filter(debt => {
    const accountType = (debt.account_type || '').toLowerCase();
    if (type === 'student') return accountType.includes('student');
    if (type === 'divorce') return accountType.includes('divorce');
    if (type === 'pension') return accountType.includes('pension');
    return false;
  }), 'balance');
  const totalSpecial = sumBy(priorityDebts.filter(debt => debt.priority_type === 'dso'), 'priority_amount')
    + sumBy(priorityDebts.filter(debt => debt.priority_type === 'tax'), 'priority_amount')
    + sumBy(priorityDebts.filter(debt => debt.priority_type === 'intoxicated'), 'priority_amount')
    + otherUnsecuredTotal('divorce')
    + otherUnsecuredTotal('student')
    + otherUnsecuredTotal('pension');

  const primaryDebtor = (caseData.debtors || []).find(debtor => debtor.is_primary && debtor.is_filing);
  const secondaryDebtor = (caseData.debtors || []).find(debtor => !debtor.is_primary && debtor.is_filing);
  const wagesAndDeductions = debtor => (debtor?.employments || []).reduce(
    (totals, employment) => ({
      wages: totals.wages + calcGrossWages(employment),
      deductions: totals.deductions + (employment.deductions || [])
        .reduce((sum, deduction) => sum + (Number(deduction.amount) || 0), 0),
    }),
    { wages: 0, deductions: 0 },
  );
  const primaryPayroll = wagesAndDeductions(primaryDebtor);
  const secondaryPayroll = wagesAndDeductions(secondaryDebtor);

  const filingDebtorCount = [primaryDebtor, secondaryDebtor].filter(Boolean).length;
  const maritalStatus = filingDebtorCount === 2
    ? 'd'
    : (caseData.debtors || []).length === 2 ? 'c'
      : primaryDebtor?.marital_status === 'separated' ? 'b' : 'a';
  const presumption = caseData.chapter === '7' && debtType === 'c' ? 'n' : '';
  const statLineSix = debtType === 'b' ? 'o' : '';
  const stateCode = primaryDebtor?.state || '';
  const householdSize = (caseData.debtors?.length || 0) + (dependents?.length || 0);
  const medianIncome = Number(medianData[STATE_NAMES[stateCode]]?.[`${householdSize}`]) || 0;

  const lines = [
    `stat||||i|${statLineSix}||${caseData.chapter}|${feeStatus}|${debtType}|v||${assetNotice}|${numCreditors}|${assetAmountStat}|${totalDebtStat}|n|||y|${presumption}|||${priorBKStat}|`
      + `${petitionData.real_property_total}|${petitionData.personal_property_total}|${totalSecuredBalance.toFixed(2)}|${totalPriorityBalance.toFixed(2)}|${totalUnsecuredBalance.toFixed(2)}|`
      + `${totalIncomeStat.toFixed(2)}|${totalExpense.toFixed(2)}|${(m1 + m2).toFixed(2)}|${totalSpecial.toFixed(2)}||`
      + `|${primaryPayroll.wages.toFixed(2)}|${secondaryPayroll.wages.toFixed(2)}|${primaryPayroll.deductions.toFixed(2)}|${secondaryPayroll.deductions.toFixed(2)}|`
      + `${(totalIncomeStat - totalExpense).toFixed(2)}|||${maritalStatus}|${m1.toFixed(2)}|${m2.toFixed(2)}|${medianIncome}|${householdSize}|||||||||||||||||||||||||||||||||`,
  ];

  for (const [debtor, role] of [[primaryDebtor, 'db'], [secondaryDebtor, 'jdb']]) {
    if (!debtor) continue;
    const fips = await fipsForDebtor(debtor);
    lines.push(
      `debt|${role}|${debtor.first_name}|${debtor.second_name || ''}|${debtor.last_name}|`
      + `|${debtor.suffix || ''}|${debtor.ssn}||2|${debtor.street}|${debtor.street2 || ''}||${debtor.city}|${debtor.state}|${debtor.zip_code}|${fips}||||`,
    );
    for (const alias of debtor.aliases || []) {
      lines.push(`alas|${role}|${alias.alias_type}|${alias.first_name}|${alias.second_name || ''}|${alias.last_name}`);
    }
  }

  return new Blob([lines.join('\n')], { type: 'text/plain' });
}
